/**
 * The browser's half of the audio way in (plan §9.2): claim the code from `/audio#code=…`, mint a
 * socket ticket before every dial, sign out. The renewal that makes the second step work is an
 * `httpOnly` cookie, so this file never sees it — it only asks the sidecar to read it.
 *
 * `credentials: 'include'` ON EVERY CALL, and that is the whole of the topology question from this
 * side. Same-origin (`/v1` routed beside the SPA) sends the cookie either way; a same-site API
 * subdomain (`api.<domain>`) sends it only when asked to, and the sidecar answers with
 * `Access-Control-Allow-Credentials` for the origins it was told to trust (`ALLOWED_ORIGINS`).
 */
import {
  AUDIO_CLAIM_PATH,
  AUDIO_CODE_FRAGMENT_KEY,
  AUDIO_SESSION_PATH,
  isAudioCode,
  isAudioPlayerName,
  isSkinHash,
  isUuidText,
  type AudioClaimAccepted,
  type AudioPlayer,
  type AudioSessionIssued,
  type UuidText,
} from '@dimensionscore/shared';

import { AudioCredentialRefused } from './channel';

export type ClaimOutcome =
  | { readonly ok: true; readonly accepted: AudioClaimAccepted }
  | { readonly ok: false; readonly reason: 'used_or_expired' | 'malformed' | 'rate_limited' | 'unreachable' };

export type SessionOutcome =
  | { readonly ok: true; readonly issued: AudioSessionIssued }
  /** `none`: this browser has no audio session (or a staff one that names no player). */
  | { readonly ok: false; readonly reason: 'none' | 'rate_limited' | 'unreachable' };

export interface AudioSessionClientOptions {
  /** The sidecar's origin; empty for same-origin (the production default). */
  readonly apiBase: string;
  readonly fetchImpl?: typeof fetch;
}

function isAudioPlayer(value: unknown): value is AudioPlayer {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  return isUuidText(p['uuid']) && isAudioPlayerName(p['name']) && (p['skin'] === null || isSkinHash(p['skin']));
}

export class AudioSessionClient {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: AudioSessionClientOptions) {
    this.base = options.apiBase.replace(/\/+$/, '');
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  /** Presents the code from the link. Single use: a second call with the same code is `used_or_expired`. */
  async claim(code: string): Promise<ClaimOutcome> {
    if (!isAudioCode(code)) return { ok: false, reason: 'malformed' };
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.base}${AUDIO_CLAIM_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
      });
    } catch {
      return { ok: false, reason: 'unreachable' };
    }
    if (response.status === 404) return { ok: false, reason: 'used_or_expired' };
    if (response.status === 429) return { ok: false, reason: 'rate_limited' };
    if (!response.ok) return { ok: false, reason: response.status === 400 ? 'malformed' : 'unreachable' };
    const body = (await response.json().catch(() => null)) as Partial<AudioClaimAccepted> | null;
    if (body === null || !isAudioPlayer(body.player) || typeof body.token !== 'string') return { ok: false, reason: 'unreachable' };
    return { ok: true, accepted: body as AudioClaimAccepted };
  }

  /**
   * A socket ticket from the renewal cookie — or, for a player signed in with Discord, from their
   * session, which also leaves a renewal behind. Called before every dial.
   */
  async session(bearer: string | null): Promise<SessionOutcome> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.base}${AUDIO_SESSION_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: bearer === null ? {} : { authorization: `Bearer ${bearer}` },
      });
    } catch {
      return { ok: false, reason: 'unreachable' };
    }
    if (response.status === 401 || response.status === 403) return { ok: false, reason: 'none' };
    if (response.status === 429) return { ok: false, reason: 'rate_limited' };
    if (!response.ok) return { ok: false, reason: 'unreachable' };
    const body = (await response.json().catch(() => null)) as Partial<AudioSessionIssued> | null;
    if (body === null || !isAudioPlayer(body.player) || typeof body.ticket !== 'string') return { ok: false, reason: 'unreachable' };
    return { ok: true, issued: body as AudioSessionIssued };
  }

  /** The channel's credential provider: a fresh ticket, or {@link AudioCredentialRefused} when there is none to be had. */
  credentialProvider(bearer: () => string | null, onIssued?: (issued: AudioSessionIssued) => void): () => Promise<string> {
    return async () => {
      const outcome = await this.session(bearer());
      if (outcome.ok) {
        onIssued?.(outcome.issued);
        return outcome.issued.ticket;
      }
      if (outcome.reason === 'none') throw new AudioCredentialRefused('this browser has no audio session any more; type /audio in game for a fresh link');
      throw new Error(outcome.reason === 'rate_limited' ? 'the sidecar asked us to slow down' : 'the sidecar was not reachable');
    };
  }

  /** Clears the renewal cookie. Best effort: signing out must never fail on the network. */
  async signOut(): Promise<void> {
    try {
      await this.fetchImpl(`${this.base}${AUDIO_SESSION_PATH}`, { method: 'DELETE', credentials: 'include' });
    } catch {
      /* nothing to undo */
    }
  }
}

/** The code in `#code=…`, if the page was opened from an in-game `/audio` link. */
export function codeFromFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.length === 0) return null;
  const code = new URLSearchParams(raw).get(AUDIO_CODE_FRAGMENT_KEY);
  return code !== null && code.length > 0 ? code : null;
}

/** A display-only identity for the page — never a credential. */
export interface AudioIdentity {
  readonly uuid: UuidText;
  readonly name: string;
  readonly skin: string | null;
}
